import { LoadingNote } from "../lib/Loading.tsx";
import { WorkThumb } from "../lib/WorkThumb.tsx";
import { VIEWING_ROOMS_COPY } from "./viewingRooms.constants.ts";
import type { PreviewView } from "./viewingRooms.model.ts";

/**
 * A glance at what a room holds.
 *
 * A count alone does not tell a collector whether they picked the right pieces,
 * and the room's own screen otherwise says "60 works" and nothing else. Six
 * thumbnails and a title each answers the question the count was standing in
 * for.
 *
 * What it shows is what the room PUBLISHES, not what it once named: the works
 * come from `ondo.my_viewing_room_works()`, which resolves through the same
 * intersection a visitor's request does. So a work that has since left the
 * collection it was in is already absent here, rather than being shown and then
 * missing from the page itself.
 */
export function RoomWorksPreview({
  preview,
  note,
}: {
  /** Null while the answer is still coming. */
  preview: PreviewView | null;
  /** Said under a live room, where what it holds can change without being edited. */
  note: string | null;
}) {
  return (
    <section className="rooms__preview">
      <h3 className="rooms__legend">{VIEWING_ROOMS_COPY.previewTitle}</h3>

      {preview === null && (
        <LoadingNote message={VIEWING_ROOMS_COPY.previewLoading} />
      )}

      {/* Empty is an answer, not a gap: a room whose works have all left the
          collections they were in publishes nothing, and it should say so. */}
      {preview !== null && preview.items.length === 0 && (
        <p className="rooms__note">{VIEWING_ROOMS_COPY.previewEmpty}</p>
      )}

      {preview !== null && preview.items.length > 0 && (
        <>
          <p className="rooms__meta">
            {preview.count}
            {preview.more ? ` · ${preview.more}` : ""}
          </p>

          <ul className="rooms__preview-list">
            {preview.items.map((item) => (
              <li key={item.id} className="rooms__preview-item">
                <span className="rooms__preview-thumb">
                  <WorkThumb imageUrl={item.imageUrl} title={item.label} />
                </span>
                <span className="rooms__preview-label">{item.label}</span>
              </li>
            ))}
          </ul>

          {note && <p className="rooms__hint">{note}</p>}
        </>
      )}
    </section>
  );
}
